import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import NotFound from '../../components/common/NotFound';
import ReCaptchaWidget from '../../components/forms/ReCaptchaWidget';
import { apiRequest, handleBranding } from '../../utils';
import '../readiness/PublicReadiness.css';

/* Public, unauthenticated "create your free account" for a Bravos storefront.
   tornea.app/create-account/<slug> — reached from the storefront's journey panel. Name + email are
   required, company and phone optional; reCAPTCHA guards the submit because it mails a typed
   address. The backend writes NOTHING here (everything rides in the signed activation token), so a
   known and an unknown email get the identical "check your inbox" screen — we never reveal which.
   Backed by GET + POST /submit (routes/public_account.py); the mailed link lands on /activate/:token. */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CreateAccount = () => {
  const { t } = useTranslation();
  const { slug } = useParams();

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState('loading');      // loading | ok | notfound
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', company: '', phone: '', hp: '',
  });
  const [consent, setConsent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!slug) { setStatus('notfound'); return undefined; }
    let cancelled = false;
    // A visitor can arrive here from a shared link having never seen the storefront, so pin the
    // brand slug BEFORE resolving branding — otherwise the whole auth surface stays Tornea's.
    localStorage.setItem('brandSlug', slug);
    handleBranding();
    apiRequest('GET', `/public-account/${slug}`)
      .then((res) => { if (!cancelled) { setConfig(res); setStatus('ok'); } })
      .catch(() => { if (!cancelled) setStatus('notfound'); });
    return () => { cancelled = true; };
  }, [slug]);

  const recaptchaSiteKey = (config && config.recaptcha_site_key) || null;
  const brandName = (config && config.bu_name) || 'Bravos';
  const accentStyle = (config && config.primary_color)
    ? { '--bravos-accent': config.primary_color } : undefined;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = t('freeAccountErrorRequired');
    if (!form.last_name.trim()) e.last_name = t('freeAccountErrorRequired');
    if (!form.email.trim()) e.email = t('freeAccountErrorRequired');
    else if (!EMAIL_RE.test(form.email.trim())) e.email = t('freeAccountErrorEmail');
    if (!consent) e.consent = t('freeAccountErrorConsent');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    if (recaptchaSiteKey && !captchaToken) { setFormError(t('completeCaptcha')); return; }
    setSubmitting(true);
    setFormError(null);
    try {
      // Raw fetch, like the other public pages: apiRequest treats a 401 as a dead session and
      // logs the visitor out, which is nonsense on a page nobody is signed in to.
      const resp = await fetch(`/api/public-account/${slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          submit_token: config.submit_token,
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          company: form.company,
          phone: form.phone,
          recaptcha_token: captchaToken,
          hp: form.hp || '',
        }),
      });
      if (resp.ok) { setSent(true); window.scrollTo(0, 0); return; }
      const data = await resp.json().catch(() => ({}));
      // reCAPTCHA tokens are single-use — clear it so a retry gets a fresh challenge.
      setCaptchaToken('');
      setFormError(data && data.error === 'ERR_CAPTCHA'
        ? t('captchaFailed') : t('freeAccountError'));
    } catch (err) {
      setCaptchaToken('');
      setFormError(t('freeAccountError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return <Screen><div className="top"><Card><span className="auth__spinner" /></Card></div></Screen>;
  }
  if (status === 'notfound') return <NotFound message={t('freeAccountNotFound')} />;

  const field = (key, labelKey, type = 'text', optional = false, autoComplete = undefined) => (
    <div className="mb-3 text-start">
      <label className="form-label fw-bold" htmlFor={`fa_${key}`}>
        {t(labelKey)}{!optional && <span className="text-danger"> *</span>}
      </label>
      <input
        id={`fa_${key}`}
        type={type}
        autoComplete={autoComplete}
        className={`form-control${errors[key] ? ' is-invalid' : ''}`}
        value={form[key]}
        onChange={(e) => setField(key, e.target.value)}
      />
      {errors[key] && <div className="invalid-feedback d-block">{errors[key]}</div>}
    </div>
  );

  return (
    <Screen><div className="top">
      <Card>
        <div className="readiness" style={accentStyle}>
          {config.logo_url && (
            <img src={config.logo_url} alt={brandName} className="readiness__logo" />
          )}

          {sent ? (
            /* Deliberately identical whether or not we already knew this address. */
            <div className="text-center py-3">
              <Title>{t('freeAccountSentTitle')}</Title>
              <p className="text-muted mx-2">
                {t('freeAccountSentBody', { email: form.email.trim() })}
              </p>
              {config.cta_url && (
                <a className="bravos-submit bravos-submit--ghost" href={config.cta_url}>
                  <Icon name="arrow-left" /> {t('back')}
                </a>
              )}
            </div>
          ) : (
            <>
              <Title>{t('freeAccountTitle', { brand: brandName })}</Title>
              <p className="text-muted text-start mx-2">{t('freeAccountIntro')}</p>

              {field('first_name', 'freeAccountFirstName', 'text', false, 'given-name')}
              {field('last_name', 'freeAccountLastName', 'text', false, 'family-name')}
              {field('email', 'freeAccountEmail', 'email', false, 'email')}
              {field('company', 'freeAccountCompany', 'text', true, 'organization')}
              {field('phone', 'freeAccountPhone', 'tel', true, 'tel')}

              {/* Honeypot — hidden from humans. */}
              <div aria-hidden="true"
                   style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
                <label htmlFor="fa_hp">{t('formLeaveBlank')}</label>
                <input type="text" id="fa_hp" tabIndex={-1} autoComplete="off"
                       value={form.hp} onChange={(e) => setField('hp', e.target.value)} />
              </div>

              <div className="mb-3 text-start">
                <div className="form-check">
                  <input className={`form-check-input${errors.consent ? ' is-invalid' : ''}`}
                         type="checkbox" id="fa_consent"
                         checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                  <label className="form-check-label" htmlFor="fa_consent">
                    {t('freeAccountConsent')}
                  </label>
                  {errors.consent && <div className="invalid-feedback d-block">{errors.consent}</div>}
                </div>
              </div>

              {recaptchaSiteKey && (
                <div className="mb-3 d-flex justify-content-center">
                  <ReCaptchaWidget
                    siteKey={recaptchaSiteKey}
                    onVerify={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken('')}
                    onError={() => setCaptchaToken('')}
                  />
                </div>
              )}

              {formError && <div className="alert alert-danger" role="alert">{formError}</div>}

              <button type="button" className="bravos-submit" onClick={submit} disabled={submitting}>
                {submitting ? <span className="auth__spinner" /> : <Icon name="user-add" />}
                {t('freeAccountSubmit')}
              </button>
              <p className="text-muted small text-center mt-2 mb-0">{t('freeAccountNoPassword')}</p>
            </>
          )}
        </div>
      </Card>
    </div></Screen>
  );
};

export default CreateAccount;
